import { observer } from 'mobx-react';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Loader, TextButtonSize } from '@wix/design-system';
import { AppContextProvider } from '../../AppLoader';
import { ThemedTextButton } from '../../ThemedComponents';
import s from './SkipEnforcementButton.scss';

interface SkipLinkProps {
  label: string;
  onClick: () => void;
  isLoading: boolean;
  size?: TextButtonSize;
  dataHook?: string;
}

/**
 * The underlined "skip" link of the redesigned post-login screens. Leaving the
 * screen proceeds to the post-auth URL (a redirect / postMessage that takes a beat),
 * so while `isLoading` is set we show a loader instead — this both gives feedback
 * and prevents a double-click.
 *
 * The link stays mounted (kept invisible) while loading and the loader is overlaid
 * on top, so the element keeps its exact footprint and the card doesn't jump/re-center
 * when the label is swapped for the (smaller) loader.
 */
export const SkipLink: React.FC<SkipLinkProps> = ({
  label,
  onClick,
  isLoading,
  size = 'small',
  dataHook,
}) => (
  <div className={s.root}>
    <span className={classNames({ [s.hidden]: isLoading })}>
      <ThemedTextButton
        dataHook={dataHook}
        size={size}
        underline="always"
        disabled={isLoading}
        onClick={onClick}
      >
        {label}
      </ThemedTextButton>
    </span>
    {isLoading && (
      <span className={s.loaderOverlay}>
        <Loader size="tiny" />
      </span>
    )}
  </div>
);

interface SkipEnforcementButtonProps {
  /** BI screen name reported with the skip click. */
  screenName: string;
  size?: TextButtonSize;
  dataHook?: string;
}

/**
 * "Skip for now" link shared by the redesigned enforcement screens.
 */
export const SkipEnforcementButton: React.FC<SkipEnforcementButtonProps> =
  observer(({ screenName, size = 'small', dataHook }) => {
    const { t } = useTranslation();
    const {
      rootStore: {
        postLoginStore: { premiumUsers2FaEnforcementStore: store },
      },
    } = useContext(AppContextProvider);

    return (
      <SkipLink
        label={t('premium.users.2fa.enforcement.skip.cta')}
        onClick={() => store.skipEnforcement(screenName)}
        isLoading={store.isSkipping}
        size={size}
        dataHook={dataHook}
      />
    );
  });
